import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, CalendarClock, Check, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDate, formatMinor } from '@/lib/format';
import {
  useDeletePayable,
  useDues,
  usePayPayable,
  useSavePayable,
  type DueItem,
  type ExpiryItem,
} from '@/api/hooks/finance';
import { useVehicles } from '@/api/hooks/catalog';
import { Badge } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ПАДЕЖИ — какво да получим, какво да платим, кои срокове изтичат.
 *
 *  Три колони, всяка подредена по дата: най-отгоре е това, което вече е
 *  просрочено, после тази седмица, после по-нататък. Числата отгоре са
 *  отговорът на въпроса „колко пари се движат тази седмица".
 * ═══════════════════════════════════════════════════════════════════════════
 */

const CATEGORY_LABEL: Record<string, string> = {
  fuel: 'Гориво',
  tolls: 'Пътни такси',
  repair: 'Ремонт',
  tyres: 'Гуми',
  insurance: 'Застраховка',
  leasing: 'Лизинг',
  subcontractor: 'Подизпълнител',
  rent: 'Наем',
  salary: 'Заплати',
  tax: 'Данъци',
  telecom: 'Телефони и интернет',
  other: 'Друго',
};

function whenLabel(days: number): { text: string; tone: 'danger' | 'warn' | 'neutral' | 'ok' } {
  if (days < 0) return { text: `просрочено ${Math.abs(days)} ${Math.abs(days) === 1 ? 'ден' : 'дни'}`, tone: 'danger' };
  if (days === 0) return { text: 'днес', tone: 'warn' };
  if (days === 1) return { text: 'утре', tone: 'warn' };
  if (days <= 7) return { text: `след ${days} дни`, tone: 'warn' };
  return { text: `след ${days} дни`, tone: 'neutral' };
}

export function DuesPage() {
  const [days, setDays] = useState(60);
  const { data, isLoading, isError, refetch } = useDues(days);
  const [adding, setAdding] = useState(false);

  return (
    <div className="flex min-h-full flex-col gap-4 p-3 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Падежи</h1>
          <p className="text-sm text-ink-dim">Какво да получим, какво да платим и кои документи изтичат.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select
            className="w-56"
            aria-label="Колко напред"
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            options={[
              { value: '14', label: 'Следващите 2 седмици' },
              { value: '30', label: 'Следващия месец' },
              { value: '60', label: 'Следващите 2 месеца' },
              { value: '120', label: 'Следващите 4 месеца' },
            ]}
          />
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
            Входящ документ
          </Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingBlock />
      ) : isError || !data ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SummaryCard
              icon={<ArrowDownLeft className="size-5" />}
              title="Да получим"
              main={formatMinor(data.totals.receivableOpenMinor, 'EUR')}
              lines={[
                { label: 'просрочени', value: formatMinor(data.totals.receivableOverdueMinor, 'EUR'), danger: data.totals.receivableOverdueMinor > 0 },
                { label: 'до 7 дни', value: formatMinor(data.totals.receivableNext7Minor, 'EUR') },
              ]}
              tone="ok"
            />
            <SummaryCard
              icon={<ArrowUpRight className="size-5" />}
              title="Да платим"
              main={formatMinor(data.totals.payableOpenMinor, 'EUR')}
              lines={[
                { label: 'просрочени', value: formatMinor(data.totals.payableOverdueMinor, 'EUR'), danger: data.totals.payableOverdueMinor > 0 },
                { label: 'до 7 дни', value: formatMinor(data.totals.payableNext7Minor, 'EUR') },
              ]}
              tone="warn"
            />
            <SummaryCard
              icon={<CalendarClock className="size-5" />}
              title="Срокове на документи"
              main={`${data.expiries.length}`}
              lines={[
                { label: 'изтекли', value: String(data.totals.expiriesOverdue), danger: data.totals.expiriesOverdue > 0 },
                { label: 'до 30 дни', value: String(data.totals.expiriesNext30) },
              ]}
              tone="info"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <DueColumn
              title="Да получим"
              subtitle="Издадени фактури, неплатени изцяло"
              items={data.receivables}
              empty="Няма неплатени фактури с падеж в периода."
            />
            <DueColumn
              title="Да платим"
              subtitle="Входящи документи — гориво, ремонти, застраховки"
              items={data.payables}
              empty="Няма входящи документи. Добавете с бутона „Входящ документ“."
              payable
            />
            <ExpiryColumn items={data.expiries} />
          </div>
        </>
      )}

      {adding ? <PayableDialog onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function SummaryCard({
  icon,
  title,
  main,
  lines,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  main: string;
  lines: { label: string; value: string; danger?: boolean }[];
  tone: 'ok' | 'warn' | 'info';
}) {
  return (
    <Card className="relative overflow-hidden p-4">
      <div
        className={cn(
          'absolute inset-y-0 left-0 w-1',
          tone === 'ok' ? 'bg-accent' : tone === 'warn' ? 'bg-warn' : 'bg-info',
        )}
      />
      <div className="flex items-center gap-2 text-sm text-ink-dim">
        {icon}
        {title}
      </div>
      <div className="mt-2 font-display text-2xl font-bold tabular">{main}</div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {lines.map((l) => (
          <span key={l.label} className={cn('text-ink-dim', l.danger && 'font-medium text-danger')}>
            {l.label}: <span className="tabular">{l.value}</span>
          </span>
        ))}
      </div>
    </Card>
  );
}

function DueColumn({ title, subtitle, items, empty, payable = false }: { title: string; subtitle: string; items: DueItem[]; empty: string; payable?: boolean }) {
  const pay = usePayPayable();
  const remove = useDeletePayable();
  return (
    <Card className="flex flex-col">
      <CardHeader title={title} subtitle={subtitle} />
      {items.length === 0 ? (
        <p className="p-5 text-sm text-ink-faint">{empty}</p>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((item) => {
            const when = whenLabel(item.daysLeft);
            return (
              <li key={item.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{item.counterparty}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-dim">
                    {item.documentNo ? <span className="font-mono">№ {item.documentNo}</span> : null}
                    {item.category ? <span>{CATEGORY_LABEL[item.category] ?? item.category}</span> : null}
                    {item.vehicle ? <span>бус {item.vehicle}</span> : null}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="text-ink-faint">падеж {formatDate(item.dueDate)}</span>
                    <Badge tone={when.tone}>{when.text}</Badge>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-medium tabular">{formatMinor(item.openMinor, item.ccy)}</div>
                  {item.paidMinor > 0 ? (
                    <div className="text-[11px] text-ink-faint">от {formatMinor(item.amountMinor, item.ccy)}</div>
                  ) : null}
                  {payable ? (
                    <div className="mt-1.5 flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<Check className="size-3.5" />}
                        loading={pay.isPending && pay.variables === item.id}
                        onClick={() =>
                          pay.mutate(item.id, { onSuccess: () => toast.ok(`Платено: ${item.counterparty}`) })
                        }
                      >
                        Платено
                      </Button>
                      <IconButton
                        label="Изтрий"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm(`Да изтрия ли документа от ${item.counterparty}?`)) remove.mutate(item.id);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </IconButton>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ExpiryColumn({ items }: { items: ExpiryItem[] }) {
  return (
    <Card className="flex flex-col">
      <CardHeader title="Срокове" subtitle="Застраховки, прегледи, винетки, книжки" />
      {items.length === 0 ? (
        <p className="p-5 text-sm text-ink-faint">Нищо не изтича в периода.</p>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((item) => {
            const when = whenLabel(item.daysLeft);
            return (
              <li key={item.id} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{item.label}</div>
                  <div className="mt-0.5 text-xs text-ink-dim">
                    {item.subject}
                    {item.reference ? ` · ${item.reference}` : ''}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-ink-dim">{formatDate(item.validUntil)}</div>
                  <Badge tone={when.tone} className="mt-1">
                    {item.daysLeft < 0 ? 'изтекъл' : when.text}
                  </Badge>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function PayableDialog({ onClose }: { onClose: () => void }) {
  const save = useSavePayable();
  const vehicles = useVehicles();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    supplierName: '',
    documentNo: '',
    category: 'fuel',
    issueDate: today,
    dueDate: today,
    amount: '',
    ccy: 'EUR',
    vehicleId: '',
    notes: '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const amountMinor = Math.round(Number(form.amount.replace(',', '.')) * 100);
  const valid = form.supplierName.trim().length >= 2 && Number.isFinite(amountMinor) && amountMinor > 0 && /^\d{4}-\d{2}-\d{2}$/.test(form.dueDate);
  const vehicleList = vehicles.data?.items ?? [];

  return (
    <Dialog open onClose={onClose} title="Входящ документ с падеж" width="lg">
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          save.mutate(
            {
              supplierName: form.supplierName.trim(),
              ...(form.documentNo.trim() ? { documentNo: form.documentNo.trim() } : {}),
              category: form.category,
              ...(form.issueDate ? { issueDate: form.issueDate } : {}),
              dueDate: form.dueDate,
              amountMinor,
              ccy: form.ccy,
              ...(form.vehicleId ? { vehicleId: form.vehicleId } : {}),
              ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
            },
            {
              onSuccess: () => {
                toast.ok('Документът е записан.');
                onClose();
              },
            },
          );
        }}
      >
        <Field label="От кого (доставчик)" required className="sm:col-span-2">
          <Input value={form.supplierName} onChange={set('supplierName')} placeholder="напр. AS24, Автосервиз Пловдив, ДЗИ" autoFocus />
        </Field>
        <Field label="Номер на документа">
          <Input value={form.documentNo} onChange={set('documentNo')} placeholder="0000012345" />
        </Field>
        <Field label="Вид разход">
          <Select
            value={form.category}
            onChange={set('category')}
            options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </Field>
        <Field label="Дата на документа">
          <Input type="date" value={form.issueDate} onChange={set('issueDate')} />
        </Field>
        <Field label="Падеж (краен срок за плащане)" required>
          <Input type="date" value={form.dueDate} onChange={set('dueDate')} />
        </Field>
        <Field label="Сума" required>
          <Input inputMode="decimal" value={form.amount} onChange={set('amount')} placeholder="0,00" />
        </Field>
        <Field label="Валута">
          <Select value={form.ccy} onChange={set('ccy')} options={['EUR', 'BGN', 'USD', 'CHF', 'PLN', 'RON', 'HUF', 'CZK'].map((c) => ({ value: c, label: c }))} />
        </Field>
        <Field label="Бус (ако е за конкретен бус)" className="sm:col-span-2">
          <Select
            value={form.vehicleId}
            onChange={set('vehicleId')}
            options={[{ value: '', label: '— не е за конкретен бус —' }, ...vehicleList.map((v) => ({ value: v.id, label: `${v.fleetNo} · ${v.plate}` }))]}
          />
        </Field>
        <Field label="Бележка" className="sm:col-span-2">
          <Textarea rows={2} value={form.notes} onChange={set('notes')} />
        </Field>
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button type="submit" variant="primary" disabled={!valid} loading={save.isPending}>
            Запиши
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
